import { 
  db, 
  auth, 
  collection, 
  doc, 
  setDoc, 
  deleteDoc,
  getDocs, 
  query, 
  where, 
  serverTimestamp, 
  handleFirestoreError, 
  OperationType 
} from '../firebase';
import { ViewerDocument, DocumentMetadata, SpreadsheetSheet } from '../types/documentViewer';

export interface FirestoreCorporateDocRecord {
  id: string;
  uid: string;
  ownerUid: string;
  userUid?: string;
  vendorProfileId: string;
  documentType: string;
  fileUrl?: string;
  fileName?: string;
  issuingAuthority: string;
  filingDate: string;
  incorporationDate?: string;
  expiryDate?: string;
  structuredData?: any;
  rawContent?: string;
  markdownContent?: string;
  status: string;
  uploadedAt: any;
  title: string;
  fileType: string;
}

/**
 * Helper to serialize structured data for Firestore (flattening nested arrays in spreadsheets)
 */
function serializeStructuredData(data: any): any {
  if (!data) return null;
  const result: any = { ...data };

  // Firestore does NOT support nested arrays (Array<Array<any>>)
  // We transform spreadsheet rows into an array of objects { rowIndex: number, values: Array<any> }
  if (result.spreadsheetData && Array.isArray(result.spreadsheetData)) {
    result.spreadsheetData = result.spreadsheetData.map((sheet: any) => ({
      name: sheet.name || 'Sheet',
      headers: Array.isArray(sheet.headers) ? sheet.headers : [],
      rows: Array.isArray(sheet.rows)
        ? sheet.rows.map((row: any, idx: number) => ({
            rowIndex: idx,
            values: Array.isArray(row) ? row : [row]
          }))
        : []
    }));
  }

  return result;
}

/**
 * Helper to deserialize structured data from Firestore back into ViewerDocument format
 */
function deserializeStructuredData(structuredData: any): {
  certificateData?: any;
  spreadsheetData?: SpreadsheetSheet[];
  presentationSlides?: any;
} {
  if (!structuredData) return {};

  let spreadsheetData: SpreadsheetSheet[] | undefined = undefined;
  if (Array.isArray(structuredData.spreadsheetData)) {
    spreadsheetData = structuredData.spreadsheetData.map((sheet: any) => ({
      name: sheet.name || 'Sheet',
      headers: Array.isArray(sheet.headers) ? sheet.headers : [],
      rows: Array.isArray(sheet.rows)
        ? sheet.rows.map((row: any) => {
            if (row && typeof row === 'object' && 'values' in row && Array.isArray(row.values)) {
              return row.values;
            }
            return Array.isArray(row) ? row : [row];
          })
        : []
    }));
  }

  return {
    certificateData: structuredData.certificateData || undefined,
    spreadsheetData,
    presentationSlides: structuredData.presentationSlides || undefined
  };
}

/**
 * Save a single corporate document or certificate to the Firestore /corporate_documents collection
 */
export async function saveCorporateDocumentToFirestore(
  document: ViewerDocument,
  vendorProfileId: string = 'default-vendor'
): Promise<string> {
  const currentUser = auth.currentUser;
  const currentUserId = currentUser?.uid || 'anonymous';
  const docId = document.id || `corp-doc-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

  const structuredData = serializeStructuredData({
    certificateData: document.certificateData || null,
    spreadsheetData: document.spreadsheetData || null,
    presentationSlides: document.presentationSlides || null,
    metadata: document.metadata || null
  });

  const firestoreRecord = {
    id: docId,
    uid: currentUserId,
    ownerUid: currentUserId,
    userUid: currentUserId,
    vendorProfileId: vendorProfileId || currentUserId,
    documentType: document.metadata?.documentType || document.fileType || 'certificate',
    title: document.title,
    fileName: document.metadata?.fileName || `${document.title.replace(/\s+/g, '_')}.${document.fileType}`,
    fileType: document.fileType,
    fileUrl: document.fileUrl || '',
    issuingAuthority: document.metadata?.issuingAuthority || 'Department of State - Division of Corporations',
    filingDate: document.metadata?.filingDate || document.metadata?.incorporationDate || new Date().toISOString().split('T')[0],
    incorporationDate: document.metadata?.incorporationDate || document.certificateData?.incorporationDate || '',
    filingNumber: document.metadata?.filingNumber || document.certificateData?.filingNumber || '',
    companyName: document.metadata?.companyName || document.certificateData?.companyName || '',
    status: document.metadata?.status || 'Active Certified Record',
    rawContent: document.rawContent || '',
    markdownContent: document.markdownContent || '',
    structuredData,
    uploadedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  try {
    const docRef = doc(db, 'corporate_documents', docId);
    await setDoc(docRef, firestoreRecord, { merge: true });
    return docId;
  } catch (error) {
    console.error('Error saving corporate document to Firestore:', error);
    handleFirestoreError(error, OperationType.WRITE, `corporate_documents/${docId}`);
    throw error;
  }
}

/**
 * Save multiple retrieved official documents to Firestore in a batch
 */
export async function saveMultipleDocumentsToFirestore(
  documents: ViewerDocument[],
  vendorProfileId: string = 'default-vendor'
): Promise<{ successCount: number; errors: any[] }> {
  let successCount = 0;
  const errors: any[] = [];

  for (const document of documents) {
    try {
      await saveCorporateDocumentToFirestore(document, vendorProfileId);
      successCount++;
    } catch (err) {
      console.error(`Failed saving doc ${document.id}:`, err);
      errors.push({ docId: document.id, error: err });
    }
  }

  return { successCount, errors };
}

/**
 * Fetch all corporate documents belonging to a vendor from Firestore
 */
export async function fetchCorporateDocumentsFromFirestore(
  vendorProfileId?: string
): Promise<ViewerDocument[]> {
  const currentUser = auth.currentUser;
  if (!currentUser?.uid) {
    return [];
  }

  try {
    const docsCollection = collection(db, 'corporate_documents');
    const q = query(docsCollection, where('uid', '==', currentUser.uid));

    const snapshot = await getDocs(q);
    const results: ViewerDocument[] = [];

    snapshot.forEach((snap) => {
      const data = snap.data();
      const deserialized = deserializeStructuredData(data.structuredData);

      results.push({
        id: snap.id,
        title: data.title || 'Official Document',
        fileType: data.fileType || 'pdf',
        fileUrl: data.fileUrl || '',
        rawContent: data.rawContent || '',
        markdownContent: data.markdownContent || '',
        certificateData: deserialized.certificateData,
        spreadsheetData: deserialized.spreadsheetData,
        presentationSlides: deserialized.presentationSlides,
        metadata: {
          id: snap.id,
          title: data.title,
          fileName: data.fileName,
          fileType: data.fileType,
          fileUrl: data.fileUrl,
          issuingAuthority: data.issuingAuthority,
          filingNumber: data.filingNumber,
          incorporationDate: data.incorporationDate,
          filingDate: data.filingDate,
          status: data.status,
          companyName: data.companyName,
          isSavedToFirestore: true,
          firestoreDocId: snap.id,
          firestoreSavedAt: data.uploadedAt?.toDate?.()?.toISOString() || new Date().toISOString()
        }
      });
    });

    return results;
  } catch (error) {
    console.error('Error fetching corporate documents from Firestore:', error);
    // Non-blocking fallback
    return [];
  }
}

/**
 * Delete a corporate document from the Firestore /corporate_documents collection
 */
export async function deleteCorporateDocumentFromFirestore(docId: string): Promise<void> {
  const currentUser = auth.currentUser;
  if (!currentUser?.uid) return;

  try {
    const docRef = doc(db, 'corporate_documents', docId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting corporate document from Firestore:', error);
    handleFirestoreError(error, OperationType.DELETE, `corporate_documents/${docId}`);
    throw error;
  }
}
